import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/Card";
import { Btn } from "@/components/Btn";
import { SectionHeader } from "@/components/SectionHeader";
import { SettingsRow, S_INPUT } from "@/components/SettingsRow";
import { ApiError } from "@/api/client";
import { SettingsConfig, getSettingsConfig, putSettingsConfig } from "@/api/settings";

type Props = { isAdmin: boolean };

type DashboardCfg = Pick<
  SettingsConfig,
  | "DASHBOARD_KONUS_LINK_FRIST_TAGE"
  | "DASHBOARD_KONUS_DATEN_FRIST_TAGE"
  | "DASHBOARD_INFOS_GRUPPE_FRIST_TAGE"
  | "DASHBOARD_RECHNUNG_EINGANG_FRIST_TAGE"
  | "DASHBOARD_ANZAHLUNG_FRIST_TAGE"
>;

const EMPTY: DashboardCfg = {
  DASHBOARD_KONUS_LINK_FRIST_TAGE: "",
  DASHBOARD_KONUS_DATEN_FRIST_TAGE: "",
  DASHBOARD_INFOS_GRUPPE_FRIST_TAGE: "",
  DASHBOARD_RECHNUNG_EINGANG_FRIST_TAGE: "",
  DASHBOARD_ANZAHLUNG_FRIST_TAGE: "",
};

export function SystemSection({ isAdmin }: Props) {
  const qc = useQueryClient();
  const configQ = useQuery({ queryKey: ["settings-config"], queryFn: getSettingsConfig });

  const [cfg, setCfg] = useState<DashboardCfg>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (configQ.data) {
      setCfg({
        DASHBOARD_KONUS_LINK_FRIST_TAGE: configQ.data.DASHBOARD_KONUS_LINK_FRIST_TAGE,
        DASHBOARD_KONUS_DATEN_FRIST_TAGE: configQ.data.DASHBOARD_KONUS_DATEN_FRIST_TAGE,
        DASHBOARD_INFOS_GRUPPE_FRIST_TAGE: configQ.data.DASHBOARD_INFOS_GRUPPE_FRIST_TAGE,
        DASHBOARD_RECHNUNG_EINGANG_FRIST_TAGE: configQ.data.DASHBOARD_RECHNUNG_EINGANG_FRIST_TAGE,
        DASHBOARD_ANZAHLUNG_FRIST_TAGE: configQ.data.DASHBOARD_ANZAHLUNG_FRIST_TAGE,
      });
      setDirty(false);
    }
  }, [configQ.data]);

  const saveMut = useMutation({
    mutationFn: () => putSettingsConfig({ ...configQ.data!, ...cfg }),
    onSuccess: async (next) => {
      setCfg({
        DASHBOARD_KONUS_LINK_FRIST_TAGE: next.DASHBOARD_KONUS_LINK_FRIST_TAGE,
        DASHBOARD_KONUS_DATEN_FRIST_TAGE: next.DASHBOARD_KONUS_DATEN_FRIST_TAGE,
        DASHBOARD_INFOS_GRUPPE_FRIST_TAGE: next.DASHBOARD_INFOS_GRUPPE_FRIST_TAGE,
        DASHBOARD_RECHNUNG_EINGANG_FRIST_TAGE: next.DASHBOARD_RECHNUNG_EINGANG_FRIST_TAGE,
        DASHBOARD_ANZAHLUNG_FRIST_TAGE: next.DASHBOARD_ANZAHLUNG_FRIST_TAGE,
      });
      setDirty(false);
      setMsg("Gespeichert.");
      await qc.invalidateQueries({ queryKey: ["settings-config"] });
    },
    onError: (e: unknown) => {
      setMsg(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen.");
    },
  });

  function set<K extends keyof DashboardCfg>(key: K, val: string) {
    setCfg((prev) => ({ ...prev, [key]: val }));
    setDirty(true);
  }

  return (
    <>
      {!isAdmin && (
        <Card style={{ marginBottom: 16, background: "var(--bg-subtle)" }}>
          <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            <span className="material-icons-outlined" style={{ fontSize: 16, verticalAlign: "-3px" }}>
              info
            </span>{" "}
            Nur lesbar — Änderungen können nur Administratoren speichern.
          </div>
        </Card>
      )}

      <Card>
        <SectionHeader
          title="Dashboard-Fristen"
          desc="Steuern, wann eine Buchung als offene Aufgabe auf dem Dashboard auftaucht."
        />
        <SettingsRow
          label="Konus-Link versenden"
          hint='Tage vor Anreise, ab denen Status "Bestätigt" ohne versandten Link auf der Aufgabenliste erscheint.'
        >
          <input
            style={{ ...S_INPUT, maxWidth: 140 }}
            type="number"
            step="1"
            min={0}
            disabled={!isAdmin}
            value={cfg.DASHBOARD_KONUS_LINK_FRIST_TAGE}
            onChange={(e) => set("DASHBOARD_KONUS_LINK_FRIST_TAGE", e.target.value)}
          />
        </SettingsRow>
        <SettingsRow
          label="Konus-Daten ausstehend"
          hint='Tage vor Anreise, ab denen Status "Konus Link versandt" ohne erfasste Daten auf der Aufgabenliste erscheint.'
        >
          <input
            style={{ ...S_INPUT, maxWidth: 140 }}
            type="number"
            step="1"
            min={0}
            disabled={!isAdmin}
            value={cfg.DASHBOARD_KONUS_DATEN_FRIST_TAGE}
            onChange={(e) => set("DASHBOARD_KONUS_DATEN_FRIST_TAGE", e.target.value)}
          />
        </SettingsRow>
        <SettingsRow
          label="Infos an Gruppe versenden"
          hint='Tage vor Anreise, ab denen Status "Konus-Daten eingegeben" ohne versandte Infos auf der Aufgabenliste erscheint.'
        >
          <input
            style={{ ...S_INPUT, maxWidth: 140 }}
            type="number"
            step="1"
            min={0}
            disabled={!isAdmin}
            value={cfg.DASHBOARD_INFOS_GRUPPE_FRIST_TAGE}
            onChange={(e) => set("DASHBOARD_INFOS_GRUPPE_FRIST_TAGE", e.target.value)}
          />
        </SettingsRow>
        <SettingsRow
          label="Rechnungseingang prüfen"
          hint='Tage seit Rechnungsdatum, ab denen Status "Rechnung erstellt" auf der Aufgabenliste erscheint.'
        >
          <input
            style={{ ...S_INPUT, maxWidth: 140 }}
            type="number"
            step="1"
            min={0}
            disabled={!isAdmin}
            value={cfg.DASHBOARD_RECHNUNG_EINGANG_FRIST_TAGE}
            onChange={(e) => set("DASHBOARD_RECHNUNG_EINGANG_FRIST_TAGE", e.target.value)}
          />
        </SettingsRow>
        <SettingsRow
          label="Anzahlung offen"
          hint="Tage vor Anreise, innerhalb derer bestätigte Buchungen ohne Anzahlungseingang auf der Aufgabenliste erscheinen."
        >
          <input
            style={{ ...S_INPUT, maxWidth: 140 }}
            type="number"
            step="1"
            min={1}
            disabled={!isAdmin}
            value={cfg.DASHBOARD_ANZAHLUNG_FRIST_TAGE}
            onChange={(e) => set("DASHBOARD_ANZAHLUNG_FRIST_TAGE", e.target.value)}
          />
        </SettingsRow>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          {msg && (
            <span style={{ fontSize: 12, color: "var(--fg-muted)", alignSelf: "center" }}>
              {msg}
            </span>
          )}
          <Btn
            variant="primary"
            icon="check"
            onClick={() => saveMut.mutate()}
            disabled={!isAdmin || !dirty || saveMut.isPending}
          >
            Speichern
          </Btn>
        </div>
      </Card>
    </>
  );
}
